import { API } from "./index";
import APIEndPoints from "../constants/APIConstants";

/** Append server-side pagination query params (?page=&size= or &page=&size=). */
const appendPagination = (url, page, size) => {
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}page=${encodeURIComponent(page)}&size=${encodeURIComponent(
    size
  )}`;
};

/**
 * transactionMediumHelper / transactionTypeHelper return one token or
 * "CHECK&medium=ACH" / "DEPOSIT&transactionType=FEE". If the whole string was
 * passed through encodeURIComponent once, it looks like CHECK%26medium%3DACH —
 * the server then sees one invalid medium. Undo that and split on both raw and
 * percent-encoded joiners, then emit repeated query keys (Play getQueryParamValues).
 */
const expandRepeatParam = (raw, paramName) => {
  if (raw == null || raw === "") {
    return [];
  }
  let s = String(raw).trim();
  try {
    if (/%[0-9A-Fa-f]{2}/.test(s) && s.includes("%26")) {
      const decoded = decodeURIComponent(s);
      if (decoded !== s) {
        s = decoded;
      }
    }
  } catch (_) {
    /* keep s */
  }
  const sep = `&${paramName}=`;
  const encSep = `%26${paramName}%3D`;
  let parts = [s];
  const splitOn = (chunks, delimiter) =>
    chunks.flatMap((chunk) => chunk.split(delimiter));
  parts = splitOn(parts, sep);
  parts = splitOn(parts, encSep);
  if (paramName === "transactionType") {
    parts = splitOn(parts, "&type=");
    parts = splitOn(parts, "%26type%3D");
  }
  return parts
    .map((p) => p.trim())
    .filter((p) => p !== "")
    .map((v) => `${paramName}=${encodeURIComponent(v)}`);
};

class UpcomingTransactionAPI extends API {
  fetchUpcomingTransactionList = (page, size, callback) => {
    const url = appendPagination(
      APIEndPoints.FETCH_UPCOMING_TRANSACTION,
      page,
      size
    );
    fetch(url, {
      method: "GET",
      headers: this.authHeaders,
    })
      .then((response) => {
        this.processResponse(response, callback);
      })
      .catch((error) => {
        console.log("Error in calling client api.", error);
        callback(null, "Service error, please try again.");
      });
  };
  getClientUpcomingTransList = (clientId, callback) => {
    fetch(
      APIEndPoints.FETCH_CLIENT_UPCOMING_TRANSACTION + clientId,

      {
        method: "GET",
        headers: this.authHeaders,
      }
    )
      .then((response) => {
        this.processResponse(response, callback);
      })
      .catch((error) => {
        console.log("Error in calling client api.", error);
        callback(null, "Service error, please try again.");
      });
  };
  fetchClientUpcomingTransactionList = (
    clientId,
    from,
    to,
    medium,
    term,
    vendor,
    type,
    callback
  ) => {
    const mediumQs =
      medium == null || medium === ""
        ? [`medium=${encodeURIComponent("")}`]
        : expandRepeatParam(medium, "medium");
    const typeQs =
      type == null || type === ""
        ? [`transactionType=${encodeURIComponent("")}`]
        : expandRepeatParam(type, "transactionType");
    const qs = [
      `from=${encodeURIComponent(from)}`,
      `to=${encodeURIComponent(to)}`,
      ...mediumQs,
      `term=${encodeURIComponent(term != null ? term : "")}`,
      `vendor=${encodeURIComponent(vendor != null ? vendor : "")}`,
      ...typeQs,
    ].join("&");
    fetch(
      `${APIEndPoints.FETCH_CLIENT_UPCOMING_TRANSACTION}${clientId}?${qs}`,
      {
        method: "GET",
        headers: this.authHeaders,
      }
    )
      .then((response) => {
        this.processResponse(response, callback);
      })
      .catch((error) => {
        console.log("Error in calling client api.", error);
        callback(null, "Service error, please try again.");
      });
  };
  fetchAnnualUpcomingTransactionList = (callback) => {
    fetch(APIEndPoints.FETCH_ANNUAL_UPCOMING_TRANSACTION, {
      method: "GET",
      headers: this.authHeaders,
    })
      .then((response) => {
        this.processResponse(response, callback);
      })
      .catch((error) => {
        console.log("Error in calling client api.", error);
        callback(null, "Service error, please try again.");
      });
  };
  processUpcomingTransaction = (scheduleTransData, callback) => {
    let body = JSON.stringify(scheduleTransData);
    fetch(APIEndPoints.PROCESS_UPCOMING_TRANSACTION, {
      method: "POST",
      headers: this.authHeaders,
      body: body,
    })
      .then((response) => {
        this.processResponse(response, callback);
      })
      .catch((error) => {
        console.log("Error in calling add User api", error);
        callback(null, "Service error, please try again.");
      });
  };
  skipUpcomingTransaction = (scheduleTransData, callback) => {
    let body = JSON.stringify(scheduleTransData);
    fetch(APIEndPoints.SKIP_UPCOMING_TRANSACTION, {
      method: "POST",
      headers: this.authHeaders,
      body: body,
    })
      .then((response) => {
        this.processResponse(response, callback);
      })
      .catch((error) => {
        console.log("Error in calling add User api", error);
        callback(null, "Service error, please try again.");
      });
  };
  searchUpcomingTransactionByTerm = (value, page, size, callback) => {
    const urlString = appendPagination(
      `${APIEndPoints.SEARCH_UPCOMING_TRANSACTION}?term=${encodeURIComponent(value)}`,
      page,
      size
    );
    fetch(urlString, {
      method: "GET",
      headers: this.authHeaders,
    })
      .then((response) => {
        this.processResponse(response, callback);
      })
      .catch((error) => {
        callback(null, "Service error, please try again.");
      });
  };
  searchAnnualUpcomingTransactionByTerm = (value, callback) => {
    const urlString =
      APIEndPoints.SEARCH_ANNUAL_UPCOMING_TRANSACTION_BY_TERM + value;
    fetch(urlString, {
      method: "GET",
      headers: this.authHeaders,
    })
      .then((response) => {
        this.processResponse(response, callback);
      })
      .catch((error) => {
        console.log("Error in calling search api.", error);
        callback(null, "Service error, please try again.");
      });
  };
  searchUpcomingTransactionByMedium = (medium, page, size, callback) => {
    const mediumQs = expandRepeatParam(medium, "medium").join("&");
    const urlString = appendPagination(
      `${APIEndPoints.SEARCH_UPCOMING_TRANSACTION}${mediumQs ? `?${mediumQs}` : ""}`,
      page,
      size
    );
    fetch(urlString, {
      method: "GET",
      headers: this.authHeaders,
    })
      .then((response) => {
        this.processResponse(response, callback);
      })
      .catch((error) => {
        console.log("Error in calling search api.", error);
        callback(null, "Service error, please try again.");
      });
  };
  searchUpcomingTransactionByDate = (from, to, page, size, callback) => {
    const urlString = appendPagination(
      `${APIEndPoints.SEARCH_UPCOMING_TRANSACTION}?from=${encodeURIComponent(
        from
      )}&to=${encodeURIComponent(to)}`,
      page,
      size
    );
    fetch(urlString, {
      method: "GET",
      headers: this.authHeaders,
    })
      .then((response) => {
        this.processResponse(response, callback);
      })
      .catch((error) => {
        console.log("Error in calling search api.", error);
        callback(null, "Service error, please try again.");
      });
  };
  searchUpcomingTransactionByMediumAndDate = (
    medium,
    from,
    to,
    vendor,
    type,
    page,
    size,
    callback
  ) => {
    const qs = [
      ...expandRepeatParam(medium, "medium"),
      ...expandRepeatParam(type, "transactionType"),
      `from=${encodeURIComponent(from)}`,
      `to=${encodeURIComponent(to)}`,
      `vendor=${encodeURIComponent(vendor != null ? vendor : "")}`,
    ].join("&");
    const urlString = appendPagination(
      `${APIEndPoints.SEARCH_UPCOMING_TRANSACTION}?${qs}`,
      page,
      size
    );
    fetch(urlString, {
      method: "GET",
      headers: this.authHeaders,
    })
      .then((response) => {
        this.processResponse(response, callback);
      })
      .catch((error) => {
        console.log("Error in calling search api.", error);
        callback(null, "Service error, please try again.");
      });
  };
  searchUpcomingTransactionByAll = (
    // startDate,
    // endDate,
    clientName,
    vendorName,
    transactionMedium,
    transactionType,
    // transactionStatus,
    transactionDate,
    scheduleDate,
    amount,
    compareType,
    term,
    page,
    size,
    callback
  ) => {
    const termTrimmed = term != null ? String(term).trim() : "";
    const termParam =
      termTrimmed !== ""
        ? `&term=${encodeURIComponent(termTrimmed)}`
        : "";
    const urlString = appendPagination(
      `${APIEndPoints.SEARCH_UPCOMING_TRANSACTION}?clientName=${encodeURIComponent(
        clientName
      )}&vendorName=${encodeURIComponent(
        vendorName
      )}&transactionMedium=${encodeURIComponent(
        transactionMedium
      )}&txnType=${encodeURIComponent(
        transactionType
      )}&transactionDate=${encodeURIComponent(
        transactionDate
      )}&scheduleDate=${encodeURIComponent(
        scheduleDate
      )}&amount=${encodeURIComponent(amount)}&compareType=${encodeURIComponent(
        compareType
      )}${termParam}`,
      page,
      size
    );
    fetch(urlString, {
      method: "GET",
      headers: this.authHeaders,
    })
      .then((response) => {
        this.processResponse(response, callback);
      })
      .catch((error) => {
        console.log("Error in calling search api.", error);
        callback(null, "Service error, please try again.");
      });
  };

  searchUpcomingTransactionByClient = (
    // startDate,
    // endDate,
    clientId,
    vendorName,
    transactionMedium,
    transactionType,
    // transactionStatus,
    transactionDate,
    scheduleDate,
    amount,
    compareType,
    page,
    size,
    callback
  ) => {
    const urlString = appendPagination(
      APIEndPoints.FETCH_CLIENT_UPCOMING_TRANSACTION +
        //   "startDate=" +
        //   startDate +
        //   "&endDate=" +
        //   endDate +

        clientId +
        "?vendorName=" +
        vendorName +
        "&transactionMedium=" +
        transactionMedium +
        // "&transactionType=" +
        "&txnType=" +
        transactionType +
        "&transactionDate=" +
        transactionDate +
        "&scheduleDate=" +
        scheduleDate +
        "&amount=" +
        amount +
        "&compareType=" +
        compareType,
      page,
      size
    );
    fetch(urlString, {
      method: "GET",
      headers: this.authHeaders,
    })
      .then((response) => {
        this.processResponse(response, callback);
      })
      .catch((error) => {
        console.log("Error in calling search api.", error);
        callback(null, "Service error, please try again.");
      });
  };

  // save config
  saveConfigUP = (data, callback) => {
    const urlString =
      APIEndPoints.SAVE_FILTER_CONFIG +
      "clientName=" +
      data.clientName +
      "&vendorName=" +
      data.vendorName +
      "&amount=" +
      data.balance +
      "&transactionMedium=" +
      data.transactionMedium +
      "&transactionType=" +
      data.transactionType +
      "&scheduleDate=" +
      data.scheduleDate +
      "&transactionDate=" +
      data.transactionDate +
      "&configName=" +
      data.configName +
      "&type=" +
      data.type +
      "&compareType=" +
      data.compareType +
      "&compareType=" +
      data.compareType +
      "&sortBy=" +
      data.sortBy;
    fetch(urlString, {
      method: "POST",
      headers: this.authHeaders,
    })
      .then((response) => {
        this.processResponse(response, callback);
      })
      .catch((error) => {
        console.log("Error in calling search api.", error);
        callback(null, "Service error, please try again.");
      });
  };
}
export default new UpcomingTransactionAPI();
